import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { WizardProvider, useWizard } from '../contexts/WizardContext';
import { Button } from '@/components/ui/button';
import { ChevronLeft, CheckCircle2, Save, RotateCcw, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { captureAttribution } from '../utils/attribution';
import SafetyPolicies from '../components/wizard/SafetyPolicies';
import ContactInfo from '../components/wizard/ContactInfo';
import OrganizationInfo from '../components/wizard/OrganizationInfo';
import SportsParticipants from '../components/wizard/SportsParticipants';
import CoverageStartDate from '../components/wizard/CoverageStartDate';
import CoveragePackage from '../components/wizard/CoveragePackage';
import ClaimsHistory from '../components/wizard/ClaimsHistory';
import FraudWarranty from '../components/wizard/FraudWarranty';
import AdditionalInsured from '../components/wizard/AdditionalInsured';
import ReviewSubmit from '../components/wizard/ReviewSubmit';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const WizardContent = () => {
  const { currentStep, totalSteps, prevStep, formData, showResume, resumeDraft, discardDraft } = useWizard();
  const hasPrefill = !!formData.risk_assessment_id;

  const steps = [
    { number: 1, title: 'Contact Information', component: <ContactInfo /> },
    { number: 2, title: 'Organization Details', component: <OrganizationInfo /> },
    { number: 3, title: 'Safety Policies', component: <SafetyPolicies /> },
    { number: 4, title: 'Sports & Participants', component: <SportsParticipants /> },
    { number: 5, title: 'Coverage Start Date', component: <CoverageStartDate /> },
    { number: 6, title: 'Coverage Package', component: <CoveragePackage /> },
    { number: 7, title: 'Claims History', component: <ClaimsHistory /> },
    { number: 8, title: 'Fraud Warranty', component: <FraudWarranty /> },
    { number: 9, title: 'Additional Insured', component: <AdditionalInsured /> },
    { number: 10, title: 'Review & Submit', component: <ReviewSubmit /> },
  ];

  const progress = (currentStep / totalSteps) * 100;

  // Resume prompt
  if (showResume) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-white border border-[#E5E7EB] rounded-xl shadow-lg p-8 relative overflow-hidden" data-testid="resume-draft-dialog">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
          <div className="flex items-center gap-3 mb-4">
            <RotateCcw className="h-6 w-6 text-[#7B6FE8]" />
            <h2 className="text-xl font-black text-[#1A1A1A] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Resume Your Application?</h2>
          </div>
          <p className="text-sm text-[#6B7280] mb-6">We found a saved draft from your previous session. Would you like to pick up where you left off?</p>
          <div className="flex gap-3">
            <Button onClick={resumeDraft} data-testid="resume-draft-btn" className="flex-1 bg-[#7B6FE8] text-white hover:bg-[#6B5CE7] font-semibold">
              <RotateCcw className="mr-2 h-4 w-4" /> Resume Draft
            </Button>
            <Button onClick={discardDraft} variant="outline" data-testid="discard-draft-btn" className="flex-1 border-[#E5E7EB] text-[#6B7280] hover:bg-[#F3F4F6]">
              <X className="mr-2 h-4 w-4" /> Start Fresh
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <header className="relative bg-[#232323] sticky top-0 z-50">
        <div className="h-[3px] bg-gradient-to-r from-[#E040FB] via-[#FF6B35] to-[#FFD93D]"></div>
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <img src="/logo-light.png" alt="Players Health" className="h-8" />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-white/40 hidden sm:inline">
                <Save className="inline h-3 w-3 mr-1" />Auto-saved
              </span>
              <span className="text-sm font-semibold text-[#7B6FE8]" style={{fontFamily: "'Inter', sans-serif"}}>
                Step {currentStep} of {totalSteps}
              </span>
            </div>
          </div>
          <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="absolute top-0 left-0 h-full rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #6B5CE7, #4ECDC4)' }}
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-12">
        {hasPrefill && currentStep <= 2 && (
          <div className="flex items-center gap-2 p-3 bg-[#ECFDF5] border border-[#A7F3D0] rounded-lg mb-4" data-testid="prefill-notice">
            <CheckCircle2 className="h-4 w-4 text-[#065F46] flex-shrink-0" />
            <p className="text-sm text-[#065F46]">Some fields have been pre-filled from your Risk Assessment. Please review and update as needed.</p>
          </div>
        )}
        <div className="mb-6">
          {currentStep > 1 && (
            <Button
              data-testid="wizard-back-btn"
              variant="ghost"
              onClick={prevStep}
              className="text-[#6B7280] hover:text-[#7B6FE8] hover:bg-[#EEF0FF]"
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
          )}
        </div>

        <div className="bg-white border border-[#E5E7EB] shadow-lg rounded-xl p-8 relative overflow-hidden">
          {/* Top gradient accent */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
          
          <div className="relative">
            <h2 className="text-2xl font-black text-[#1A1A1A] uppercase tracking-wide mb-2" style={{fontFamily: "'Barlow Condensed', sans-serif"}}>
              {steps[currentStep - 1].title}
            </h2>
            <div className="mt-6 step-enter">
              {steps[currentStep - 1].component}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ApplicationWizard = ({ directAccess = false }) => {
  const { affiliateSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [affiliate, setAffiliate] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Capture UTM lead-source attribution on landing (covers /apply/:slug and /quote/sports)
  useEffect(() => {
    captureAttribution();
  }, []);

  useEffect(() => {
    const loadAffiliate = async () => {
      if (directAccess) {
        const ref = searchParams.get('ref');
        if (ref) {
          try {
            const response = await axios.get(`${API_URL}/affiliates/${ref}`);
            setAffiliate(response.data);
          } catch (e) {
            setAffiliate({
              id: 'players-health-direct',
              name: 'Players Health',
              flow_template: 'AMATEUR_SPORTS_V1'
            });
          }
        } else {
          setAffiliate({
            id: 'players-health-direct',
            name: 'Players Health',
            flow_template: 'AMATEUR_SPORTS_V1'
          });
        }
      } else {
        try {
          const response = await axios.get(`${API_URL}/affiliates/${affiliateSlug}`);
          setAffiliate(response.data);
        } catch (error) {
          toast.error('Affiliate partner not found');
          navigate('/');
          return;
        }
      }

      // Fetch prefill data from risk assessment if available
      const riskAssessmentId = searchParams.get('risk_assessment_id');
      if (riskAssessmentId) {
        try {
          const res = await axios.get(`${API_URL}/risk-assessment/${riskAssessmentId}/prefill`);
          setPrefillData(res.data);
        } catch (err) {
          // Non-blocking — just proceed without prefill
        }
      }

      setLoading(false);
    };
    loadAffiliate();
  }, [affiliateSlug, navigate, directAccess, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#232323]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4ECDC4]"></div>
      </div>
    );
  }

  if (!affiliate) return null;

  return (
    <WizardProvider affiliateId={affiliate.id} affiliateName={affiliate.name} initialData={prefillData}>
      <WizardContent />
    </WizardProvider>
  );
};

export default ApplicationWizard;
